import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import { Stack } from "react-bootstrap";
import { useTranslation, Trans } from "react-i18next";
import ActionDropdown from "../ActionDropdown";
import ConfirmModal from "../ConfirmModal";

/**
 * SkeletonCard - Skeleton loading state for responsive cards
 */
const SkeletonCard = ({ columnCount = 4 }) => {
  return (
    <div className="card mb-2 shadow-sm">
      {/* Header Skeleton */}
      <div className="card-body border-bottom pb-2 mb-0">
        <div className="d-flex justify-content-between align-items-center">
          <div
            className="skeleton"
            style={{ width: "80px", height: "24px" }}
          ></div>
          <div
            className="skeleton"
            style={{ width: "40px", height: "40px", borderRadius: "8px" }}
          ></div>
        </div>
      </div>

      <div className="card-body p-4 pt-3">
        <Row className="g-3">
          {Array.from({ length: columnCount }).map((_, index) => (
            <Col key={index} xs={12} sm={6} md={4} lg={3}>
              <Stack gap={1}>
                <div
                  className="skeleton"
                  style={{ height: "16px", width: "60%", marginBottom: "4px" }}
                ></div>
                <div
                  className="skeleton"
                  style={{ height: "20px", width: "80%" }}
                ></div>
              </Stack>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

/**
 * ResponsiveCard - A reusable card component for displaying table data in mobile/tablet views
 *
 * @param {Object} props
 * @param {Object} props.data - The row data object
 * @param {Array<{key: string, name: string, render?: function, colSize?: Object}>} props.columns - Array of column definitions
 * @param {Array} props.actions - Optional array of action dropdown items
 * @param {string} props.headerKey - Optional key for displaying header identifier
 * @param {ReactNode|function} props.headerRender - Optional custom header component/render function
 * @param {Array} props.visibleColumnsList - List of visible columns (from useTableColumns hook)
 * @param {boolean} props.isLoading - Show skeleton loading state
 * @param {number} props.skeletonCount - Number of skeleton columns to show
 */
export const ResponsiveCard = ({
  data,
  columns,
  actions = [],
  headerKey,
  headerRender,
  visibleColumnsList,
  isLoading = false,
  skeletonCount = 4,
  index,
  style,
}) => {
  const visibleKeys = visibleColumnsList.map((col) => col.key);

  // Show skeleton when loading
  if (isLoading) {
    return <SkeletonCard columnCount={skeletonCount} />;
  }

  // Get column render function if exists
  const getColumnRender = (columnKey) => {
    const column = columns.find((col) => col.key === columnKey);
    if (!column) {
      return (value) => value ?? "-";
    }
    if (column.render) {
      return (value) => column.render(value, data, index);
    }
    return (value) => value ?? "-"
  };

  // Get column colSize if exists, otherwise use default
  const getColSize = (columnKey) => {
    const defaultColSize = { xs: 6, sm: 6, md: 4, lg: 3 };
    const column = columns.find((col) => col.key === columnKey);
    return column?.colSize
      ? { ...defaultColSize, ...(column?.colSize ?? {}) }
      : defaultColSize;
  };

  // Check if we should show header section
  const showHeader =
    actions && actions.length > 0 && visibleKeys.includes("actions");

  // Get header content
  const getHeaderContent = () => {
    if (headerRender) {
      return typeof headerRender === "function"
        ? headerRender(data)
        : headerRender;
    }
    if (headerKey && visibleKeys.includes(headerKey)) {
      const renderFn = getColumnRender(headerKey);
      const value = data[headerKey];
      const renderedValue = renderFn(value);
      return (
        <span className="H6 text-primary">
          #{renderedValue}
        </span>
      );
    }
    return null;
  };

  const headerContent = getHeaderContent();

  // Filter columns to only visible ones and exclude actions
  const visibleColumns = columns.filter(
    (col) => visibleKeys.includes(col.key) && col.key !== "actions"
  );

  return (
    <div className="card mb-2 shadow-sm" style={style}>
      {/* Header with Serial Number and Actions */}
      {showHeader && (
        <div className="card-body border-bottom pb-2 mb-0">
          <Stack
            direction="horizontal"
            gap={2}
            className="H5 justify-content-between align-items-center"
          >
            {headerContent && (
              <div className="d-flex align-items-center gap-2">
                {headerContent}
              </div>
            )}
            <div style={{ marginInlineStart: "auto" }}>
              <ActionDropdown
                items={actions}
                buttonClassName="outline btn-icon btn-xs"
              />
            </div>
          </Stack>
        </div>
      )}

      <div className="card-body p-4 pt-3">
        <Row className="g-3">
          {visibleColumns.map((column) => {
            // Skip if this is the headerKey field AND we're showing it in the header
            if (column.key === headerKey && showHeader && headerContent) {
              return null;
            }

            const renderFn = column.render
              ? (value) => column.render(value, data, index)
              : (value) => value ?? "-";
            const colSize = getColSize(column.key);
            const value = data[column.key];

            return (
              <Col key={column.key} {...colSize}>
                <Stack gap={1}>
                  <div className="H7 text-secondary mb-1">{column.name}</div>
                  <div
                    className="H6 text-primary"
                    dir={column.dir || undefined}
                  >
                    {renderFn(value)}
                  </div>
                </Stack>
              </Col>
            );
          })}
        </Row>
      </div>
    </div>
  );
};

/**
 * ResponsiveCardWithConfirm - Wrapper for ResponsiveCard that includes confirm modal functionality
 * Useful for items that need delete confirmation
 */
export const ResponsiveCardWithConfirm = ({
  data,
  columns,
  actions = [],
  headerKey,
  headerRender,
  visibleColumnsList,
  confirmActions,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { t } = useTranslation();

  const {
    isPending: isDeleting,
    confirmFunction,
    deleteIcon,
    i18nKey,
  } = confirmActions;

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    confirmFunction(
      { id: data.id, port: data.port || "JED" },
      {
        onSuccess: () => {
          setShowDeleteModal(false);
        },
        onError: () => {
          setShowDeleteModal(false);
        },
      }
    );
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Wrap actions to include delete confirmation
  const wrappedActions = actions.map((action) => {
    if (action.key === "delete") {
      return {
        ...action,
        onClick: handleDelete,
      };
    }
    return action;
  });

  return (
    <>
      <ResponsiveCard
        data={data}
        columns={columns}
        actions={wrappedActions}
        headerKey={headerKey}
        headerRender={headerRender}
        visibleColumnsList={visibleColumnsList}
      />
      <ConfirmModal
        isOpen={showDeleteModal}
        onToggle={cancelDelete}
        onConfirm={confirmDelete}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey={i18nKey || "CONFIRM_DELETE"}
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={deleteIcon}
      />
    </>
  );
};

export default ResponsiveCard;
